/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useTenantScope } from "../../hooks/useTenantScope";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { FloatLabel } from "primereact/floatlabel";
import { useGetEventTypesQuery } from "../../store/services/events-types";
import type { EventStatusFilter, EventDateSortOrder } from "../../store/slices/eventsFiltersSlice";
import { parseEventDateParam, toEventDateParam } from "../../store/slices/eventsFiltersSlice";
import { DateRangeFilter } from "./DateRangeFilter";

interface EventFiltersProps {
  eventId?: number | undefined;
  setEventId?: React.Dispatch<React.SetStateAction<number | undefined>>;
  startDate?: string;
  setStartDate?: React.Dispatch<React.SetStateAction<string | undefined>>;
  endDate?: string;
  setEndDate?: React.Dispatch<React.SetStateAction<string | undefined>>;
  eventName?: string;
  setEventName?: React.Dispatch<React.SetStateAction<string>>;
  eventTypeId?: number | undefined;
  setEventTypeId?: React.Dispatch<React.SetStateAction<number | undefined>>;
  eventSubTypeId?: number | undefined;
  setEventSubTypeId?: React.Dispatch<React.SetStateAction<number | undefined>>;
  featured?: boolean;
  setFeatured?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  eventStatus?: EventStatusFilter;
  setEventStatus?: React.Dispatch<React.SetStateAction<EventStatusFilter>>;
  dateSortOrder?: EventDateSortOrder;
  onToggleSortOrder?: () => void;
  onClearAll?: () => void;
}

export const EventFilters = ({
  eventId,
  setEventId = () => { },
  eventName,
  setEventName = () => { },
  startDate,
  setStartDate = () => { },
  endDate,
  setEndDate = () => { },
  eventTypeId,
  setEventTypeId = () => { },
  eventSubTypeId,
  setEventSubTypeId = () => { },
  featured,
  setFeatured = () => { },
  eventStatus = "all",
  setEventStatus = () => { },
  dateSortOrder = "asc",
  onToggleSortOrder,
  onClearAll,
}: EventFiltersProps) => {
  /**
   * PR #175 — el catálogo de tipos/subtipos es por tenant. El tenant ya no
   * llega por prop porque INC-02 eliminó el filtro local: sale del selector
   * global del header, que es la única fuente que queda.
   */
  const tenantScope = useTenantScope();
  const { data: dataEventsTypes, isLoading: isLoadingEventTypes } = useGetEventTypesQuery(
    tenantScope.tenantId ? { tenantId: tenantScope.tenantId } : undefined
  );

  const handleType =
    (name: "type" | "sub") =>
      ({ value }: DropdownChangeEvent) => {
        if (name === "type") {
          setEventTypeId(value);
          setEventSubTypeId(undefined);
        }
        if (name === "sub") setEventSubTypeId(value);
      };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date ? toEventDateParam(date) : undefined);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date ? toEventDateParam(date) : undefined);
  };

  const handleEventId = ({ value }: InputNumberChangeEvent) =>
    setEventId(value || undefined);

  return (
    <div className="grid mt-3 row-gap-3">
      {/* Fila 1 — Nombre · ID · Tipo · Sub-Tipo, los cuatro a 3 columnas.

          Grilla pareja de 4: los ocho campos entran en dos filas exactas de 12,
          así el rango de fechas sube a la fila 2 completo y abajo queda solo la
          fila de acciones. El límite de resultados se fue: lo reemplaza la
          paginación del listado.

          Si se agrega o saca un filtro, revisar que cada fila siga sumando 12:
          cuando no cierra, los campos se corren un lugar y el rango de fechas
          termina partido entre dos filas. */}
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <InputText
            id="eventName"
            name="eventName"
            onChange={(e) => setEventName(e.currentTarget.value)}
            value={eventName}
            className="w-full"
          />
          <label htmlFor="eventName">Nombre</label>
        </FloatLabel>
      </div>

      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <InputNumber
            id="eventId"
            name="eventId"
            onChange={handleEventId}
            value={eventId ?? null}
            className="w-full"
            inputClassName="w-full"
          />
          <label htmlFor="eventId">ID #</label>
        </FloatLabel>
      </div>

      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            id="eventTypeId"
            name="eventTypeId"
            onChange={handleType("type")}
            value={eventTypeId}
            optionLabel="eventTypeName"
            optionValue="eventTypeId"
            options={Array.isArray(dataEventsTypes) ? dataEventsTypes : []}
            loading={isLoadingEventTypes}
            className="w-full"
            showClear
          />
          <label htmlFor="eventTypeId">Tipo</label>
        </FloatLabel>
      </div>

      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            id="eventSubTypeId"
            name="eventSubTypeId"
            onChange={handleType("sub")}
            value={eventSubTypeId}
            optionLabel="eventSubTypeName"
            optionValue="eventSubTypeId"
            options={[
              ...((Array.isArray(dataEventsTypes) &&
                dataEventsTypes.find((item) => item.eventTypeId === eventTypeId)?.subTypes) || []),
            ]}
            emptyMessage="Seleccioná un tipo primero"
            className="w-full"
            showClear
          />
          <label htmlFor="eventSubTypeId">Sub-Tipo</label>
        </FloatLabel>
      </div>

      {/* Fila 2 — Estado · Destacados · Fecha desde · Fecha hasta. */}
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            id="eventStatus"
            name="eventStatus"
            value={eventStatus}
            onChange={(e) => setEventStatus(e.value ?? "all")}
            options={[
              { label: "Todos", value: "all" },
              { label: "Activos", value: "active" },
              { label: "Inactivos", value: "inactive" },
              { label: "Finalizados", value: "finished" },
            ]}
            optionLabel="label"
            optionValue="value"
            className="w-full"
          />
          <label htmlFor="eventStatus">Estado</label>
        </FloatLabel>
      </div>

      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            id="featured"
            name="featured"
            value={featured === undefined ? "all" : featured}
            onChange={(e) => setFeatured(e.value === "all" ? undefined : e.value)}
            options={[
              { label: "Todos", value: "all" },
              { label: "Destacados", value: true },
              { label: "No destacados", value: false },
            ]}
            optionLabel="label"
            optionValue="value"
            className="w-full"
          />
          <label htmlFor="featured">Destacados</label>
        </FloatLabel>
      </div>

      {/* Cierra la fila 2: las dos fechas juntas, que es como se leen. */}
      <DateRangeFilter
        variant="filter"
        dateFrom={parseEventDateParam(startDate)}
        dateTo={parseEventDateParam(endDate)}
        onDateFromChange={handleStartDateChange}
        onDateToChange={handleEndDateChange}
        startDateId="startDate"
        endDateId="endDate"
        labelFrom="Fecha desde"
        labelTo="Fecha hasta"
        columnClassName="col-12 md:col-3"
      />

      {/* Fila 3 — solo acciones, pegadas al borde derecho.
          `md:col` (sin número) ocupa lo que sobra de la fila en vez de un ancho
          fijo: hoy la fila es suya sola, y si mañana se agrega un filtro que la
          acorte, los botones siguen contra el borde derecho igual. Con un ancho
          fijo quedaban alineados a la derecha DE ESE BLOQUE, o sea a media
          pantalla. */}
      <div className="col-12 md:col flex flex-wrap justify-content-end align-items-center gap-2">
        <Button
          type="button"
          label={dateSortOrder === "asc" ? "Fecha ascendente" : "Fecha descendente"}
          icon={dateSortOrder === "asc" ? "pi pi-sort-amount-up" : "pi pi-sort-amount-down"}
          severity="secondary"
          outlined
          size="small"
          onClick={onToggleSortOrder}
          aria-label="Alternar orden por fecha"
        />
        {/* Botón de verdad, no `text`: al lado del toggle de orden, un link
            gris no se leía como una acción disponible. */}
        <Button
          type="button"
          label="Limpiar filtros"
          icon="pi pi-filter-slash"
          severity="secondary"
          outlined
          size="small"
          onClick={onClearAll}
        />
      </div>
    </div>
  );
};
